import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
} from 'react';
import en from '../i18n/en';
import es from '../i18n/es';
import {
  detectBrowserLocale,
  formatMessage,
  LANGUAGE_STORAGE_KEY,
  SUPPORTED_LOCALES,
} from '../i18n/utils';

const translations = { en, es };

const LanguageContext = createContext(null);

function readStoredPreference() {
  try {
    const stored = localStorage.getItem(LANGUAGE_STORAGE_KEY);
    if (stored && SUPPORTED_LOCALES.includes(stored)) return stored;
  } catch {
    /* ignore */
  }
  return null;
}

function writeStoredPreference(lang) {
  try {
    localStorage.setItem(LANGUAGE_STORAGE_KEY, lang);
  } catch {
    /* ignore */
  }
}

function resolveLanguage() {
  return readStoredPreference() ?? detectBrowserLocale();
}

export function LanguageProvider({ children }) {
  const [language, setLanguage] = useState(resolveLanguage);

  useEffect(() => {
    document.documentElement.lang = language;
  }, [language]);

  const t = useMemo(
    () => translations[language] ?? translations.en,
    [language]
  );

  const changeLanguage = useCallback((lang) => {
    if (!SUPPORTED_LOCALES.includes(lang)) return;
    setLanguage(lang);
    writeStoredPreference(lang);
    document.documentElement.lang = lang;
  }, []);

  const applyDeviceLanguage = useCallback(() => {
    const deviceLang = detectBrowserLocale();
    setLanguage(deviceLang);
    document.documentElement.lang = deviceLang;
  }, []);

  const restorePreferredLanguage = useCallback(() => {
    const preferred = resolveLanguage();
    setLanguage(preferred);
    document.documentElement.lang = preferred;
  }, []);

  const value = useMemo(
    () => ({
      language,
      changeLanguage,
      applyDeviceLanguage,
      restorePreferredLanguage,
      t,
      formatMessage,
      isSpanish: language === 'es',
      locale: language === 'es' ? 'es-ES' : 'en-US',
    }),
    [language, changeLanguage, applyDeviceLanguage, restorePreferredLanguage, t]
  );

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const ctx = useContext(LanguageContext);
  if (!ctx) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return ctx;
}
